import $ from 'cash-dom';
import {subscribe, cLog, onReady} from '../humans/helper';

/**
 * Lazy Image component
 * @package KAA
 * @author Stefan Rueschenberg <Stefan@Humans-Machines.com>
 */
export default class ImgFade {
    /**
     * Singleton instance of the component
     */
    static #instance: ImgFade;

    /**
     * UI references
     */
    protected ui = {
        fadeImages: '.media-box--img-fade img',
    };

    /**
     * Constructor
     */
    protected constructor() {
        this.registerEvents();
    }

    /**
     * Returns the singleton instance of the component
     */
    public static getInstance(): ImgFade {
        if (!ImgFade.#instance) {
            ImgFade.#instance = new ImgFade();
        }

        return ImgFade.#instance;
    }

    /**
     * Register required events
     */
    protected registerEvents(): void {
        // Initialize fade images when DOM is ready
        onReady(() => {
            // cLog('ImgFade: Initializing on page load');
            this.setupImages(this.ui.fadeImages);
        });

        // Initialize fade images when modal content is loaded
        subscribe('modal.content-loaded', (_topic, payload: { $modal: any }) => {
            // cLog('ImgFade: modal.content-loaded event received', payload);
            const modal = payload.$modal?.get ? payload.$modal.get(0) : payload.$modal;
            this.setupImages(this.ui.fadeImages, modal);
        });

        // Initialize fade images when listing is updated (lazy loading)
        subscribe('listing.updated', (_topic, payload: { $container: any }) => {
            // cLog('ImgFade: listing.updated event received', payload);
            const container = payload.$container?.get ? payload.$container.get(0) : payload.$container;
            this.setupImages(this.ui.fadeImages, container);
        });
    }

    /**
     * Applies the fade logic for all images within the given context
     *
     * @param selector CSS selector for the image elements
     * @param context Optional root DOM node to scope the search (default: document)
     */
    protected setupImages(selector: string, context: HTMLElement | Document = document): void {
        const $images = $(selector, context);
        const contextName = context === document ? 'document' : (context as HTMLElement).id || (context as HTMLElement).className || 'unknown';

        // cLog(`ImgFade: Found ${$images.length} image(s) in context:`, contextName);

        $images.each((_, image) => {
            if (!(image instanceof HTMLImageElement)) return;

            if (!image.complete) {
                // Image is still loading → wait for load event
                // cLog('ImgFade: Setting up image (waiting for load)', image);
                this.setupImg(image);
            } else {
                // Image already loaded → apply immediately
                // cLog('ImgFade: Image already loaded, applying fade immediately', image);
                this.fadeImageLoad({ currentTarget: image } as unknown as Event);
            }
        });
    }

    /**
     * Setup fade images
     */
    protected setupImg(img: HTMLImageElement): void {
        const $img = $(img);
        $img.parents('.media-box').addClass('media-box--img-fade-waiting');
        $img.one('load', this.fadeImageLoad.bind(this));
        $img.one('error', this.fadeImageError.bind(this));
    }

    /**
     * Handle fade image loading
     */
    protected fadeImageLoad(event: Event): void {
        const $img = $(event.currentTarget as HTMLImageElement);
        const $mediaBox = $img.parents('.media-box');
        if ($mediaBox.length > 0) {
            $mediaBox.removeClass('media-box--img-fade-waiting')
                .addClass('media-box--img-fade-loaded');
        }

        // Remove error callback
        $img.off('error');
    }

    /**
     * Handle fade image error
     */
    protected fadeImageError(event: Event): void {
        const $img = $(event.currentTarget as HTMLImageElement);
        const $mediaBox = $img.parents('.media-box');
        if ($mediaBox.length > 0) {
            $mediaBox.removeClass('media-box--img-fade-waiting')
                .addClass('media-box--img-fade-error');
        }

        // Remove load callback
        $img.off('load');
    }
}
